import { ValidationArea, ValidationProblemResponse } from '@ydistri/api-sdk';
import { apiSlice, ErrorLike, errorTransform, TemplateOrCalculation } from './api';
import { getTags } from './apiLib';
import { getSelectedProjectShortName, PROJECT_SLUG_HEADER } from './apiHeader';
import { serializeQueryArgsCustom } from '../lib/project/projectLib';
import { CalculationsCollection, TemplatesCollection } from '../swagger/collections';
import { signalrClient } from '../signalr/client';
import { EntityType, IProjectSignal, ITemplateSignal } from '../signalr/signalrInterfaces';
import { SignalProjectConfigurationChanged } from '../screens/ProjectAdministration/common/administrationItemsTypes';

const { TAGS, TAGS_ARRAY } = getTags('validationProblems');

/** The tag type of every area report; a mutation that can touch any template's report invalidates it whole. */
export const VALIDATION_PROBLEMS_TAG = TAGS.validationProblems;

export interface ValidationProblemsQuery {
  templateOrCalculation: TemplateOrCalculation;
  area: ValidationArea;
  /**
   * The project the report is asked for, captured when the query starts. The cache key, the tags and
   * the request's project header all come from it - never from the project current when the entry
   * refetches or answers - so a report started before a project switch keeps its own project's
   * identity and asks its own project.
   */
  project: string | undefined;
}

/** The argument of one area report for the project the tab shows now. */
export const reportQuery = (
  templateOrCalculation: TemplateOrCalculation,
  area: ValidationArea,
): ValidationProblemsQuery => ({
  templateOrCalculation,
  area,
  project: getSelectedProjectShortName(),
});

/**
 * The cache tag of one area report, or - without an area - of every report of the template. A report
 * provides both, so a mutation in one area invalidates that area and a change to the shared
 * calculation configuration invalidates them all.
 *
 * With a project the tag names that project's entry: entries and signal listeners use it, so a tab
 * that switched project keeps the old project's entries untouched by the new project's signals.
 * Without a project it names the template's entries in every project: a mutation names a template,
 * not a project, and may complete after a switch. The shown project's report refetches; the old
 * project's leftover has no subscriber and is evicted.
 */
export const validationProblemsTag = (
  templateOrCalculation: TemplateOrCalculation,
  area?: ValidationArea,
  project?: string,
): { type: typeof TAGS.validationProblems; id: string } => ({
  type: TAGS.validationProblems,
  id: [
    project ?? '*',
    templateOrCalculation.type,
    templateOrCalculation.id,
    ...(area ? [area] : []),
  ].join('-'),
});

/**
 * Which area a configuration signal is about. An entity the table does not name - the shared
 * calculation configuration, a configuration loaded from a calculation (its signal carries no entity),
 * anything the backend adds later - touches every area.
 */
export const areasOfEntity = (entityType: string | undefined): ValidationArea[] | 'all' => {
  if (entityType === undefined) return 'all';
  switch (entityType) {
    case 'Category':
    case 'EntityListConfiguration':
    case 'TargetListConfiguration':
      return [ValidationArea.Categories];
    case 'Region':
    case 'RegionFlowGroup':
    case 'RegionFlowGroupConfiguration':
      return [ValidationArea.Regions];
    case 'SkuClass':
      return [ValidationArea.SkuClasses];
    case 'ProductListConfiguration':
      return [ValidationArea.ProductLists];
    case 'BrandConfiguration':
      return [ValidationArea.Brands];
    case 'Warehouse':
    case 'WarehouseType':
      return [ValidationArea.StoresAndDepartments];
    default:
      return 'all';
  }
};

/** The areas that read entity lists: an archived or retyped list changes what they report. */
const listAreas = [ValidationArea.Categories, ValidationArea.ProductLists];

interface ConfigurationSignal extends ITemplateSignal, Partial<IProjectSignal> {
  entityType?: EntityType;
}

/** A project signal about an entity list or a target list, whatever the operation. */
export const isAboutLists = (
  signal: Pick<SignalProjectConfigurationChanged, 'entityType'>,
): boolean => signal.entityType === 'EntityList' || signal.entityType === 'TargetList';

const firstOf = (
  signal: ConfigurationSignal | ConfigurationSignal[],
): ConfigurationSignal | undefined => (Array.isArray(signal) ? signal[0] : signal);

export const apiValidationProblems = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getValidationProblems: builder.query<ValidationProblemResponse[], ValidationProblemsQuery>({
        // The argument carries the project: the slice-wide key would prefix the project current at
        // the time of a refetch, and file a report started before a switch under the new project.
        serializeQueryArgs: ({ endpointName, queryArgs }) =>
          serializeQueryArgsCustom(endpointName, queryArgs),
        queryFn: async ({ templateOrCalculation, area, project }) => {
          // The request asks the entry's own project, not the client's current default header.
          const params = project ? { headers: { [PROJECT_SLUG_HEADER]: project } } : {};
          try {
            const { data } = await (templateOrCalculation.type === 'Template'
              ? TemplatesCollection.getTemplateValidationProblems(
                  templateOrCalculation.id,
                  area,
                  params,
                )
              : CalculationsCollection.getCalculationValidationProblems(
                  templateOrCalculation.id,
                  area,
                  params,
                ));
            return { data: data.data };
          } catch (error) {
            // Returned, not thrown: a thrown error has no tags, so a failed report could never be
            // refetched by an invalidation and would stay failed until the page reloads.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
            return { error: errorTransform(error as ErrorLike) };
          }
        },
        providesTags: (result, error, arg) => [
          validationProblemsTag(arg.templateOrCalculation, undefined, arg.project),
          validationProblemsTag(arg.templateOrCalculation, arg.area, arg.project),
          validationProblemsTag(arg.templateOrCalculation),
          validationProblemsTag(arg.templateOrCalculation, arg.area),
        ],
        async onCacheEntryAdded({ templateOrCalculation, area, project }, api) {
          // A calculation's configuration never changes; only a template's report goes stale.
          if (templateOrCalculation.type !== 'Template') return;

          // The project this entry was created for is in its argument: the entry outlives a project
          // switch by keepUnusedDataFor, and the new project's signals are not about it.

          // Registered before the first load answers, so a report whose first request failed is
          // refetched by the next configuration change instead of staying failed.
          const invalidate = () =>
            api.dispatch(
              apiSlice.util.invalidateTags([
                validationProblemsTag(templateOrCalculation, area, project),
              ]),
            );
          const isOurs = (payload: ConfigurationSignal | ConfigurationSignal[]) =>
            firstOf(payload)?.templateId === templateOrCalculation.id &&
            firstOf(payload)?.projectSlug === project;

          // A configuration signal names an entity, and through it the area; an unknown entity is
          // every area. The rules signals carry no entity and are about the stores area. A project
          // signal about an entity list is about every template's list areas.
          const onConfiguration = (payload: ConfigurationSignal | ConfigurationSignal[]) => {
            if (!isOurs(payload)) return;
            const areas = areasOfEntity(firstOf(payload)?.entityType);
            if (areas === 'all' || areas.includes(area)) invalidate();
          };
          const onRules = (payload: ConfigurationSignal | ConfigurationSignal[]) => {
            if (isOurs(payload) && area === ValidationArea.StoresAndDepartments) invalidate();
          };
          const onProject = (signal: SignalProjectConfigurationChanged) => {
            if (
              signal.projectSlug === project &&
              isAboutLists(signal) &&
              listAreas.includes(area)
            ) {
              invalidate();
            }
          };

          signalrClient.on('TemplateConfigurationChanged', onConfiguration);
          signalrClient.on('TemplateConfigurationsChanged', onConfiguration);
          signalrClient.on('ConfigurationRulesChanged', onRules);
          signalrClient.on('ConfigurationRulesReset', onRules);
          signalrClient.on('ProjectConfigurationChanged', onProject);
          await api.cacheEntryRemoved;
          signalrClient.off('TemplateConfigurationChanged', onConfiguration);
          signalrClient.off('TemplateConfigurationsChanged', onConfiguration);
          signalrClient.off('ConfigurationRulesChanged', onRules);
          signalrClient.off('ConfigurationRulesReset', onRules);
          signalrClient.off('ProjectConfigurationChanged', onProject);
        },
      }),
    }),
  });

export const { useGetValidationProblemsQuery } = apiValidationProblems;
